import { Tag } from 'antd';
import { type ColumnType } from 'antd/lib/table/interface';
import { observer } from 'mobx-react';
import React from 'react';

import type DocumentFolder from 'stores/DocumentFolder';
import { camelCaseToTitle } from 'utils/String';

import StarSwitch from '../../StarSwitch/StarSwitch';
import { searchFilterFields } from '../../Table/Column';
import Table from '../../Table/Table';
import OptionsMenu from './OptionsMenu/OptionsMenu';
import {
  makeBooleanSorter,
  makeStringFilter,
  makeStringSorter,
  makeTimestampSorter
} from 'utils/TableUtils';
import { formatDateTimeMedSeconds } from 'utils/Dates';

// A folder's type as a title-cased label ('dynamic' surfaces as 'Creative').
// Used for both the Type column's display and its sort key.
function typeLabel(record: DocumentFolder): string {
  return record.data.type === 'dynamic'
    ? 'Creative'
    : camelCaseToTitle(record.data.type);
}

function getTableColumns(
  parentDocument: DocumentFolder
): Array<ColumnType<DocumentFolder>> {
  return [
    {
      title: 'Starred',
      render: (_value, record) => <StarSwitch store={record} />,
      width: 32,
      sorter: makeBooleanSorter((r) => r.isStarred)
    },

    {
      title: 'Name',
      render: (_value, record) => record.data.name,
      sorter: makeStringSorter((r) => r.data.name),
      ...searchFilterFields,
      onFilter: makeStringFilter((r) => r.name)
    },

    {
      title: 'Tags',
      render: (_value, record) =>
        (record.data.tags || []).map((tag: string) => (
          <Tag key={tag}>{tag}</Tag>
        )),
      filters: parentDocument.documentsTags.map((tag) => ({
        text: tag,
        value: tag
      })),
      onFilter: (value, record) =>
        typeof value === 'string' && record.data.tags.includes(value)
    },

    {
      title: 'Type',
      render: (_value, record) => typeLabel(record),
      sorter: makeStringSorter(typeLabel),
      ...searchFilterFields,
      onFilter: makeStringFilter((r) => r.name)
    },

    {
      title: 'Modified',
      defaultSortOrder: 'descend',
      render: (row) => formatDateTimeMedSeconds(row.data?.dateModified),
      sorter: makeTimestampSorter((r) => r.data.dateModified)
    },

    {
      title: '',
      width: 50,
      render: (_value, record) => (
        <OptionsMenu documentId={record.id} parentDocument={parentDocument} />
      )
    }
  ];
}

export interface ChildrenTableProps {
  actionButtons?: React.ReactNode;
  parentDocument: DocumentFolder;
  title?: string;
}

function ChildrenTable({
  actionButtons,
  parentDocument,
  title,
}: ChildrenTableProps) {
  const data = [...parentDocument.documents.allAvailable];
  const isLoading = parentDocument.documents.loading;

  const columns = getTableColumns(parentDocument);

  return (
    <Table
      columnsDefinition={columns}
      data={data}
      headerButtons={actionButtons}
      isLoading={isLoading}
      loadingLabel=""
      noDataLabel=""
      onRowClick={(record) => {
        try {
          parentDocument.documents.getById(record.id)?.navigateTo();
        } catch (e) {
          console.warn(e);
        }
      }}
      onRowMiddleClick={(record) => window.open(record.url, '_blank')}
      title={title ?? 'Children'}
    />
  );
}

export default observer(ChildrenTable);
